import React, { useMemo, useState } from 'react';
import { Box, Typography, Chip, Paper, Collapse, IconButton } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useNavigate } from 'react-router-dom';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HistoryIcon from '@mui/icons-material/History';
import { Referral, ReferralStage, ReferralSource, ReferralStatus, FlagSeverity } from '~/types/transition';
import { useTransitionContext } from '~/hooks/transition/useTransitionContext';
import { useTransitionAttention } from '~/providers/TransitionAttentionProvider';
import SourceBadge from '../common/SourceBadge';

const STAGES = [ReferralStage.PROCESSING, ReferralStage.DECISION, ReferralStage.EXECUTION] as const;

const STAGE_LABELS: Record<ReferralStage, string> = {
  [ReferralStage.INTAKE]: 'Intake',
  [ReferralStage.PROCESSING]: 'Processing',
  [ReferralStage.DECISION]: 'Decision',
  [ReferralStage.EXECUTION]: 'Execution',
};

const STATUS_LABELS: Record<ReferralStatus, string> = {
  [ReferralStatus.RECEIVED]: 'Received',
  [ReferralStatus.PROCESSING]: 'Processing',
  [ReferralStatus.PROCESSING_COMPLETE]: 'Processing complete',
  [ReferralStatus.AWAITING_INFO]: 'Awaiting info',
  [ReferralStatus.RECOMMENDATION_READY]: 'Ready for decision',
  [ReferralStatus.PENDING_REVIEW]: 'Pending review',
  [ReferralStatus.ACCEPTED]: 'Accepted',
  [ReferralStatus.DECLINED]: 'Declined',
  [ReferralStatus.AUTO_DECLINED]: 'Auto-declined',
  [ReferralStatus.BLOCKED]: 'Blocked',
};

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const ReferralRow: React.FC<{ referral: Referral }> = ({ referral }) => {
  const navigate = useNavigate();
  const { isUnseen } = useTransitionAttention();
  const unseen = isUnseen(referral.id);
  const blocking = referral.flags.filter((f) => f.severity === FlagSeverity.BLOCKING);
  const warning = referral.flags.filter((f) => f.severity === FlagSeverity.WARNING);
  const hasBlocker = blocking.length > 0;
  const hasWarning = warning.length > 0;

  return (
    <Box
      onClick={() => navigate(`/transition/referral/${referral.id}`)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        px: 2,
        py: 1.25,
        gap: 2,
        cursor: 'pointer',
        borderBottom: '1px solid',
        borderColor: 'divider',
        borderLeft: '3px solid',
        borderLeftColor: hasBlocker ? 'error.main' : hasWarning ? 'warning.main' : 'transparent',
        '&:hover': { bgcolor: 'action.hover' },
        transition: 'background-color 0.1s',
      }}
    >
      <Box sx={{ width: 8, flexShrink: 0, display: 'flex', alignItems: 'center' }}>
        {unseen && (
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main' }} />
        )}
      </Box>

      <Typography
        variant="body2"
        fontWeight={unseen ? 700 : 500}
        noWrap
        sx={{ flex: '0 0 180px' }}
      >
        {referral.patient.name}
      </Typography>

      <Box sx={{ flex: '0 0 72px' }}>
        <SourceBadge source={referral.source} size="small" />
      </Box>

      <Box sx={{ flex: '0 0 96px' }}>
        {(referral.priorEncounters?.length ?? 0) > 0 ? (
          <Chip
            icon={<HistoryIcon sx={{ fontSize: '0.8rem !important' }} />}
            label="Returning"
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.72rem', height: 22, fontWeight: 600, color: 'text.secondary', borderColor: 'divider' }}
          />
        ) : (
          <Chip
            label="New"
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.72rem', height: 22, color: 'text.disabled', borderColor: 'divider' }}
          />
        )}
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }} noWrap>
        {STATUS_LABELS[referral.status]}
      </Typography>

      <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
        {hasBlocker && (
          <Chip
            icon={<ErrorIcon sx={{ fontSize: '0.75rem !important' }} />}
            label={blocking.length}
            size="small"
            color="error"
            sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700 }}
          />
        )}
        {!hasBlocker && hasWarning && (
          <Chip
            icon={<WarningAmberIcon sx={{ fontSize: '0.75rem !important' }} />}
            label={warning.length}
            size="small"
            color="warning"
            sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700 }}
          />
        )}
      </Box>

      <Box sx={{ flex: '0 0 80px', textAlign: 'right', flexShrink: 0 }}>
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
          {timeAgo(referral.updatedAt)}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', fontSize: '0.62rem', color: 'text.disabled', opacity: 0.7 }}>
          rcvd {timeAgo(referral.receivedAt)}
        </Typography>
      </Box>

      <ChevronRightIcon sx={{ fontSize: '1rem', color: 'text.disabled', flexShrink: 0 }} />
    </Box>
  );
};

interface Props {
  filterSource: ReferralSource | 'ALL';
  filterStage: ReferralStage | 'ALL';
}

const PipelineGroupedList: React.FC<Props> = ({ filterSource, filterStage }) => {
  const { referrals } = useTransitionContext();
  const { isUnseen } = useTransitionAttention();

  const filtered = useMemo(() => {
    const result = referrals.filter((r) => {
      if (r.status === ReferralStatus.DECLINED || r.status === ReferralStatus.AUTO_DECLINED) return false;
      if (filterSource !== 'ALL' && r.source !== filterSource) return false;
      if (filterStage !== 'ALL' && r.stage !== filterStage) return false;
      return true;
    });
    return [...result].sort((a, b) => {
      const au = isUnseen(a.id) ? 0 : 1;
      const bu = isUnseen(b.id) ? 0 : 1;
      if (au !== bu) return au - bu;
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
  }, [referrals, filterSource, filterStage, isUnseen]);

  const visibleStages = filterStage === 'ALL' ? STAGES : [filterStage as ReferralStage];
  const [collapsed, setCollapsed] = useState<Set<ReferralStage>>(new Set());

  const toggle = (stage: ReferralStage) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(stage) ? next.delete(stage) : next.add(stage);
      return next;
    });

  return (
    <Box sx={{ flex: 1, overflowY: 'auto' }}>
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        {visibleStages.map((stage, i) => {
          const rows = filtered.filter((r) => r.stage === stage);
          const isCollapsed = collapsed.has(stage);
          return (
            <Box key={stage}>
              <Box
                onClick={() => toggle(stage)}
                sx={{
                  px: 2,
                  py: 0.875,
                  bgcolor: 'grey.50',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { bgcolor: 'grey.100' },
                }}
              >
                <Typography
                  variant="overline"
                  fontWeight={700}
                  sx={{ letterSpacing: '0.08em', lineHeight: 1, flex: 1 }}
                >
                  {STAGE_LABELS[stage]}
                </Typography>
                <Chip
                  label={rows.length}
                  size="small"
                  variant="outlined"
                  sx={{ height: 18, fontSize: '0.65rem' }}
                />
                <IconButton size="small" sx={{ p: 0, ml: 0.5 }}>
                  {isCollapsed
                    ? <ExpandMoreIcon sx={{ fontSize: '1rem', color: 'text.disabled' }} />
                    : <ExpandLessIcon sx={{ fontSize: '1rem', color: 'text.disabled' }} />}
                </IconButton>
              </Box>

              <Collapse in={!isCollapsed}>
                {rows.length === 0 ? (
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{ display: 'block', px: 2, py: 2 }}
                  >
                    No referrals
                  </Typography>
                ) : (
                  rows.map((r) => <ReferralRow key={r.id} referral={r} />)
                )}
              </Collapse>

              {i < visibleStages.length - 1 && (
                <Box sx={{ height: 8, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }} />
              )}
            </Box>
          );
        })}
      </Paper>
    </Box>
  );
};

export default PipelineGroupedList;
